import React, { useCallback, useState } from 'react';
import { createUseStyles } from 'react-jss';
import {
  Routes,
  Route,
  useMatch,
  useParams,
  useNavigate,
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';

import { AnalyticsCategoryProvider, Categories } from '@app/analytics';

import Tabs from '@mui/material/Tabs';

import { Theme } from '@app/theme';
import { Feature, SessionStatus, StudyType, useCapabilities } from '@app/api';
import {
  useSession,
  useIsAdmin,
  useDeleteSession,
  useArchiveSession,
  useRestoreSession,
} from 'queries';

import {
  OverlayLoader,
  BackButton,
  StatusLabel,
  SquareButton,
  NotFound,
  DotsMenu,
  MenuItem,
  useToast,
  BillingInformationModalContext,
} from 'components';

import { Planning } from './Planning';
import { ParticipantMonitoring } from './ParticipantMonitoring';
import { InvitationQueue } from './InvitationQueue';
import { SessionLinks } from './components/SessionLinks';
import { SearchInput } from './components/SearchInput';
import { AdminMonitoring } from './AdminMonitoring';
import { RecruitmentMonitoring } from './RecruitmentMonitoring';
import { Stats } from './Stats';
import { Chip } from '@app/core';
import { Tab } from 'components';
import { ParticipationsDetails } from 'pages/Dashboard/Sessions/Details/ParticipationsDetails';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    backgroundColor: theme.colors.grayBackground,
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    backgroundColor: theme.colors.white,
    boxShadow: theme.boxShadow.base,
    zIndex: 1,
  },
  headerLine: {
    display: 'flex',
    alignItems: 'center',

    padding: [[theme.marginBase * 3, theme.marginBase * 6]],
    paddingBottom: theme.marginBase * 2,
    [theme.media.tablet]: {
      padding: [[theme.marginBase * 3, theme.marginBase * 4]],
    },
    [theme.media.mobile]: {
      padding: [[theme.marginBase * 3, theme.marginBase * 2]],
    },
  },
  tabletTitleLine: {
    alignItems: 'center',
    marginTop: theme.marginBase,
    marginBottom: theme.marginBase * 2,
    paddingRight: theme.marginBase * 4,
    paddingLeft: theme.marginBase * 4,

    display: 'none',
    [theme.media.tablet]: {
      display: 'flex',
    },
    [theme.media.mobile]: {
      paddingRight: theme.marginBase * 2,
      paddingLeft: theme.marginBase * 2,
    },
  },
  spacer: {
    display: 'none',
    [theme.media.tablet]: {
      display: 'block',
      flex: 1,
    },
  },
  back: {
    marginRight: theme.marginBase * 3,
  },
  hideOnTablet: {
    [theme.media.tablet]: {
      display: 'none',
    },
  },
  title: {
    ...theme.fonts.h4,
    marginLeft: theme.marginBase,
    flex: 1,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    marginRight: theme.marginBase * 2,
  },
  squareButton: {
    marginRight: theme.marginBase,
  },
  settingsButton: {
    marginLeft: theme.marginBase,
  },
  hidden: {
    display: 'none',
  },
  tabsContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  tabs: {
    flex: 1,
    marginLeft: 2 * theme.marginBase,
    [theme.media.tablet]: {
      marginLeft: 0,
    },
  },
  search: {
    marginLeft: theme.marginBase * 2,
    marginRight: theme.marginBase * 2,
    width: theme.marginBase * 40,
    [theme.media.tablet]: {
      display: 'none',
    },
  },
  tabLabelChip: {
    minHeight: 'auto',
  },
}));

export const SessionDetails = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const toast = useToast();
  const { id } = useParams();
  const navigate = useNavigate();
  const match = useMatch(`/sessions/details/:id/:path/*`);
  const path = match?.params?.path || '';

  const { data: session, isLoading: fetching } = useSession(id);
  const capabilities = useCapabilities(session?.planId);
  const isAdmin = useIsAdmin();
  const canSeeQuotas = isAdmin || (capabilities && capabilities[Feature.QUOTA]);

  const handleTabChange = useCallback(
    (_: any, path: string) => navigate(path),
    [navigate],
  );

  const onError = useCallback((e: Error) => toast.exception(e), [toast]);
  const onSuccess = useCallback(() => toast.saved(), [toast]);
  const redirectToList = useCallback(() => navigate('/'), [navigate]);

  const { mutate: onDelete } = useDeleteSession(id, {
    onSuccess: redirectToList,
    onError,
  });

  const { mutate: onArchive } = useArchiveSession(id, {
    onSuccess,
    onError,
  });
  const { mutate: onRestore } = useRestoreSession(id, {
    onSuccess,
    onError,
  });

  const [search, setSearch] = useState('');

  if (fetching && !session) {
    return <OverlayLoader />;
  }

  if (!session || !!session.deletedAt) {
    return <NotFound />;
  }

  return (
    <AnalyticsCategoryProvider category={Categories.STUDY} sessionId={id}>
      <BillingInformationModalContext
        session={session}
        showAllPriceInfo
        duringSessionProcess
      >
        <div className={classes.container}>
          <div className={classes.header}>
            <div className={classes.headerLine}>
              <BackButton color="ink" to="/sessions" className={classes.back} />

              <StatusLabel
                status={session.status}
                className={classes.hideOnTablet}
              />
              <h1 className={classnames(classes.title, classes.hideOnTablet)}>
                {session.internalName ||
                  session.title ||
                  t('sessions.details.noTitle')}
              </h1>
              <div className={classes.spacer} />
              <SquareButton
                icon="ri-edit-line"
                to={`/sessions/edit/${session.id}`}
                color={session.published ? 'accent' : 'green'}
                variant={session.published ? 'muted' : 'plain'}
                className={classes.squareButton}
              />
              {session.published && <SessionLinks sessionId={session.id} />}
              <DotsMenu className={classes.settingsButton}>
                {session.status === SessionStatus.DRAFT && (
                  <MenuItem
                    label={t('sessions.details.settings.delete')}
                    onClick={() => onDelete()}
                  />
                )}
                {!session.archivedAt ? (
                  <MenuItem
                    label={t('sessions.details.settings.archive')}
                    onClick={() => onArchive()}
                  />
                ) : (
                  <MenuItem
                    label={t('sessions.details.settings.restore')}
                    onClick={() => onRestore()}
                  />
                )}
              </DotsMenu>
            </div>

            <div className={classes.tabletTitleLine}>
              <StatusLabel status={session.status} />
              <h1 className={classes.title}>
                {session.internalName ||
                  session.title ||
                  t('sessions.details.noTitle')}
              </h1>
            </div>
            <div className={classes.tabsContainer}>
              <Tabs
                value={path}
                onChange={handleTabChange}
                aria-label="Menu"
                indicatorColor="primary"
                textColor="primary"
                className={classes.tabs}
              >
                {session?.studyType === StudyType.MODERATED ? (
                  <Tab
                    label={t('sessions.details.monitoring.title')}
                    icon="ri-kanban-view"
                    value=""
                  />
                ) : (
                  <Tab
                    label={t('sessions.details.recruitmentMonitoring.title')}
                    icon="ri-bar-chart-box-line"
                    value=""
                  />
                )}

                <Tab
                  label={t('sessions.details.participantsDetails.title')}
                  labelInner={
                    <Chip className={classes.tabLabelChip} color="blue">
                      {t('sessions.details.monitoring.chips.new')}
                    </Chip>
                  }
                  icon="ri-table-view"
                  value="participants-details"
                />

                {session?.studyType === StudyType.MODERATED && (
                  <Tab
                    label={t('sessions.details.overview.title')}
                    icon="ri-calendar-event-line"
                    value="planning"
                  />
                )}

                {isAdmin && (
                  <Tab
                    label={t('sessions.details.invitationQueue.title')}
                    value="invitation-queue"
                  />
                )}
                {isAdmin && (
                  <Tab
                    label={t('sessions.details.monitoringAdmin.tab')}
                    value="monitoring-admin"
                  />
                )}

                {((isAdmin && session?.studyType !== StudyType.MODERATED) ||
                  session?.id === '723a973c-c203-48b8-865a-c6f457e87440') && (
                  <Tab
                    label={t('sessions.details.monitoring.title')}
                    value="participant-monitoring"
                  />
                )}
                {isAdmin && (
                  <Tab label={t('sessions.details.stats.tab')} value="stats" />
                )}
              </Tabs>
              {((path === '' && session.studyType === StudyType.MODERATED) ||
                path === 'participant-monitoring') && (
                <div className={classes.search}>
                  <SearchInput onChange={setSearch} />
                </div>
              )}
            </div>
          </div>
          <Routes>
            <Route
              path="*"
              element={
                session.studyType === StudyType.MODERATED ? (
                  <ParticipantMonitoring
                    session={session}
                    search={search}
                    canSeeQuotas={canSeeQuotas}
                  />
                ) : (
                  <RecruitmentMonitoring
                    session={session}
                    canSeeQuotas={canSeeQuotas}
                  />
                )
              }
            />
            <Route
              path="participants-details/*"
              element={
                <ParticipationsDetails
                  session={session}
                  canSeeQuotas={canSeeQuotas}
                />
              }
            />
            <Route
              path="participant-monitoring/*"
              element={
                <ParticipantMonitoring
                  session={session}
                  search={search}
                  canSeeQuotas={canSeeQuotas}
                />
              }
            />
            <Route
              path="recruitment-monitoring/*"
              element={
                <RecruitmentMonitoring
                  session={session}
                  canSeeQuotas={canSeeQuotas}
                />
              }
            />
            <Route
              path="invitation-queue/*"
              element={<InvitationQueue session={session} />}
            />
            <Route path="planning/*" element={<Planning session={session} />} />
            <Route
              path="monitoring-admin/*"
              element={<AdminMonitoring session={session} />}
            />
            <Route path="stats/*" element={<Stats session={session} />} />
          </Routes>
        </div>
      </BillingInformationModalContext>
    </AnalyticsCategoryProvider>
  );
};
